/**
 * Soft barge-in: the user spoke OVER the agent — keep talking (a listener cue, agreement, a laugh) or cut and give them
 * the floor (a question, correction, "wait", new information)? Rules decide the clear cases at 0ms; a fast yes/no model
 * decides the rest within a budget. Undecided (no model, failed, late) → 'cut': today's hard barge is the safe default.
 */
import { forComponent } from '../logging';
import type { ChatLike, ChatResponse } from '../llm';

const log = forComponent('Overlap');

export type OverlapVerdict = 'keep' | 'cut';
export interface OverlapDecision { verdict: OverlapVerdict; by: 'rule' | 'model' | 'late' | 'error' | 'none'; ms: number }

const norm = (s: string) => s.toLowerCase().replace(/[’']/g, "'").replace(/[^\p{L}\p{N}' -]+/gu, ' ').replace(/\s+/g, ' ').trim();
/** Wants the floor, whatever else is said: stop/hold words, a correction opener, a question word. */
const HARD = /\b(?:stop|wait|no|nope|nah|hold on|hang on|hold up|pause|cancel|quiet|enough|shut up|never ?mind|actually|but|sorry what|what|why|how|who|when|where|which|can you|could you|tell me|i mean|excuse me)\b/;
/** Listener tokens: acks, agreement, reactions, laughter. An overlap made ONLY of these is a cue, not a turn. */
const ACK = new Set(('yeah yea yes yep yup ya ok okay k right sure true totally exactly definitely absolutely indeed ' +
  'mm mmm hmm mhm mm-hm uh-huh uh huh aha ah oh ooh wow nice cool great awesome perfect good fine ' +
  'interesting gotcha got it i see makes sense fair agreed correct of course for sure lol ha haha hahaha heh hehe ' +
  'yeah yeah alright all right sweet neat oh nice oh wow oh cool oh okay oh yeah oh right that\'s right that\'s true ' +
  'thats right thats true love it i know me too same').split(' '));
/** Fillers / conjunctions a listener cue may lead with ("and yeah", "um okay", "so right"). */
const LEAD = new Set(['and', 'so', 'um', 'uh', 'er', 'well', 'oh']);
/** Standalone acks that can open a cue (ACK also holds phrase fragments like "that's", "i", "of" — not leaders). */
const LEADER = new Set(['yeah', 'yea', 'yes', 'yep', 'yup', 'ok', 'okay', 'right', 'sure', 'alright', 'mm', 'mhm', 'hmm', 'uh-huh', 'totally', 'exactly', 'true']);
const PHRASES = ['got it', 'i see', 'makes sense', 'of course', 'for sure', 'all right', "that's right", "that's true", 'thats right', 'thats true', 'love it', 'i know', 'me too', 'uh huh'];

/** A short go-on cue ("and?", "really?", "and then?") — a question that asks us to keep going, not for the floor. */
const CONTINUE = /^(?:(?:and|so|oh) )?(?:and|so|really|seriously|then what|and then|go on|keep going|ואז|באמת|נו)$/;
/** Agreement ("yeah", "exactly", "that's right", "כן", "נכון") — what a spoken aside answers ("Oh yeah, definitely."). */
const AGREE = new Set(('yeah yea yes yep yup right sure true totally exactly definitely absolutely agreed correct indeed ' +
  'כן נכון בדיוק ברור בטח אכן לגמרי מסכים מסכימה ממש').split(' '));
const AGREE_PHRASES = ['of course', 'for sure', "that's right", "that's true", 'thats right', 'thats true', 'makes sense', 'i agree'];
/** Hebrew listener tokens (the English ACK set never matched a Hebrew cue — it always waited on the model). */
const HE_ACK = new Set('כן נכון בדיוק ברור בטח אכן לגמרי ממש אוקיי אוקי סבבה יפה מעולה וואו אהה אה הממ מממ'.split(' '));

/** What kind of cue a KEPT overlap is: agreement or a go-on question get a spoken aside; anything else (mm-hm, okay, a
 *  laugh) is just the listener listening — answering it would be odd. */
export function cueKind(text: string): 'agree' | 'continue' | null {
  const t = norm(text);
  if (!t) return null;
  if (CONTINUE.test(t)) return 'continue';
  let r = t;
  for (const p of AGREE_PHRASES) if (r.includes(p)) return 'agree';
  r = r.split(/[\s-]+/).filter((w) => !LEAD.has(w)).join(' ');
  return r.split(' ').some((w) => AGREE.has(w)) ? 'agree' : null;
}

/** The fill channel's aside for a kept cue: ≤4 words, never a claim, in the cue's (else the call's) language; null =
 *  stay quiet (not agreement/go-on, or a language with no lines). `avoid` = the last aside (no back-to-back repeats). */
export const ASIDES: Record<string, Record<'agree' | 'continue', string[]>> = {
  en: { agree: ['Oh yeah, definitely.', 'Yeah, exactly.', 'Right, yeah.'], continue: ['Yeah, so…', 'So, yeah…'] },
  he: { agree: ['כן, בדיוק.', 'כן, ממש.', 'נכון, כן.'], continue: ['כן, אז…', 'אז כן…'] },
};
export function overlapAside(text: string, lang: string | null, avoid = '', pick: (n: number) => number = (n) => Math.floor(Math.random() * n)): string | null {
  const kind = cueKind(text);
  const l = /\p{Script=Hebrew}/u.test(text) ? 'he' : /[a-z]/i.test(text) ? 'en' : lang;
  const pool = kind && l ? ASIDES[l]?.[kind] : undefined;
  if (!pool?.length) return null;
  const fresh = pool.filter((x) => x !== avoid);
  return fresh[pick(fresh.length)] ?? null;
}

/** 0ms verdict for the clear cases; undefined = ambiguous (ask the model). */
export function overlapRule(text: string): OverlapVerdict | undefined {
  if (CONTINUE.test(norm(text))) return 'keep'; // "and?", "really?" — go on (before the '?' rule)
  if (/\?/.test(text)) return 'cut';
  let t = norm(text);
  if (!t) return undefined;
  if (HARD.test(t)) return 'cut';
  for (const p of PHRASES) t = t.split(p).join(' ');
  const words = t.split(/[\s-]+/).filter(Boolean);
  const ack = (w: string) => ACK.has(w) || HE_ACK.has(w) || /^(?:ha)+h?$|^(?:he)+h?$|^m+(?:hm+)?$|^h?m+$/.test(w);
  if (words.every(ack)) return 'keep';
  // Ack-led with at most one trailing word ("And yeah, I", "yeah so", "okay and"): still a cue — live, "And yeah, I"
  // went to the model, came back late and cut the reply. If the line grows into content the engine re-decides it.
  const lead = words.findIndex((w) => !LEAD.has(w));
  if (lead >= 0 && LEADER.has(words[lead]) && words.slice(lead).filter((w) => !ack(w) && !LEAD.has(w)).length <= 1) return 'keep';
  return undefined;
}

/** Fast yes/no: is this overlap only a listener cue? (groq qwen, reasoning off: ~100-150ms — see fastChannelDecider.) */
export function fastOverlapModel(ai: ChatLike, model = 'groq/qwen/qwen3.8-27b'): (heard: string, signal: AbortSignal) => Promise<OverlapVerdict | null> {
  return async (heard, signal) => {
    const prompt = 'Live voice call. The assistant is mid-sentence and the user just said this over it: "' + heard + '"\n' +
      'Is it ONLY a listener cue — agreement, acknowledgement, a short reaction or laugh — so the assistant should keep talking? ' +
      'Answer no if they want the floor: a question, objection, correction, request, or new information.\nAnswer yes or no.';
    const reasoning = /qwen/.test(model) ? { providerOptions: { reasoning_effort: 'none' } } : /gpt-oss/.test(model) ? { providerOptions: { reasoning_effort: 'low' } } : {};
    const r = (await ai.chat({ model, messages: [{ role: 'user', content: prompt }], stream: false, signal, temperature: 0, maxTokens: /gpt-oss/.test(model) ? 200 : 3, ...reasoning })) as ChatResponse;
    const a = (r.content ?? '').trim().toLowerCase();
    return /^yes/.test(a) ? 'keep' : /^no/.test(a) ? 'cut' : null;
  };
}

export class OverlapJudgeOptions {
  /** The fast model for ambiguous overlaps; absent → ambiguous cuts. */
  model?: (heard: string, signal: AbortSignal) => Promise<OverlapVerdict | null>;
  /** A model verdict later than this is dropped → cut. 600ms: groq qwen measured p50 ~100ms / max ~230ms in isolation
   *  (24 calls, 2026-10-14) but live, beside the reflex + chatty-channel calls, 2 of 2 ambiguous verdicts missed 300ms
   *  and cut the reply. The engine holds the overlap up to 900ms (bindOverlapInstinct) — a slightly later keep beats
   *  a cut, so the budget sits well inside that hold. */
  budgetMs = 600;
  onDecision: (heard: string, d: OverlapDecision) => void = () => {};
  now: () => number = () => performance.now();
}

/** Rules first, then the fast model within budget; anything undecided cuts (logged). Never rejects. */
export class OverlapJudge {
  public constructor(public options?: Partial<OverlapJudgeOptions>) {
    this.options = { ...new OverlapJudgeOptions(), ...options };
  }
  private get o(): OverlapJudgeOptions { return this.options as OverlapJudgeOptions; }

  async judge(heard: string): Promise<OverlapDecision> {
    const t0 = this.o.now();
    const done = (verdict: OverlapVerdict, by: OverlapDecision['by']) => {
      const d = { verdict, by, ms: Math.round(this.o.now() - t0) };
      if (by === 'late' || by === 'error' || by === 'none') log.warn(`overlap "${heard.slice(0, 60)}": no verdict (${by}, ${d.ms}ms) → cut`);
      else log.debug(`overlap "${heard.slice(0, 60)}": ${verdict} by ${by} ${d.ms}ms`);
      this.o.onDecision(heard, d);
      return d;
    };
    const rule = overlapRule(heard);
    if (rule) return done(rule, 'rule');
    if (!this.o.model) return done('cut', 'none');
    const ctl = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<'late'>((r) => { timer = setTimeout(() => { ctl.abort(); r('late'); }, this.o.budgetMs); });
    try {
      const v = await Promise.race([this.o.model(heard, ctl.signal).catch((e) => { if (!ctl.signal.aborted) log.warn(`overlap model failed: ${(e as Error).message}`); return 'error' as const; }), late]);
      if (v === 'late' || v === 'error') return done('cut', v);
      return v ? done(v, 'model') : done('cut', 'error');
    } finally { clearTimeout(timer); }
  }
}

/** The fast overlap model hosts wire (AGENTX_CHANNEL_DECIDER_MODEL, else groq qwen with GROQ_API_KEY — as channelDecide).
 *  Warmed at once: live, the first verdict paid the cold connection/model start and missed its 300ms budget (aborted → cut). */
export function overlapModelFor(ai: ChatLike, env: Record<string, string | undefined> = process.env): OverlapJudgeOptions['model'] {
  const model = env.AGENTX_CHANNEL_DECIDER_MODEL || undefined;
  if (!model && !env.GROQ_API_KEY) return undefined;
  const m = fastOverlapModel(ai, model);
  const t0 = performance.now();
  void m('yeah', AbortSignal.timeout(10_000)).then(
    () => log.debug(`fast model warm in ${Math.round(performance.now() - t0)}ms`),
    (e) => log.warn(`fast model warm-up failed (the first overlap verdict may be late): ${(e as Error)?.message ?? e}`));
  return m;
}

/** Soft barge-in per `--channel-mode`: turn keeps today's hard barge; normal/chatty judge overlaps. */
export const softBarge = (mode?: string): boolean => mode !== 'turn';
